/**
 * Impersonation Context
 * Manages teacher impersonation of learners
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface Learner {
  id: number;
  name: string;
  student_id: string;
  grade_level: number;
}

interface ImpersonationSession {
  is_impersonating: boolean;
  learner: Learner | null;
  expires_at: string | null;
  session_id: number | null;
  teacher: {
    id: number;
    name: string;
  } | null;
}

interface ImpersonationContextType {
  session: ImpersonationSession;
  startImpersonation: (learnerId: number, reason?: string) => Promise<void>;
  endImpersonation: () => Promise<void>;
  checkStatus: () => Promise<void>;
  loading: boolean;
  error: string | null;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error('useImpersonation must be used within ImpersonationProvider');
  }
  return context;
};

interface ImpersonationProviderProps {
  children: ReactNode;
}

export const ImpersonationProvider: React.FC<ImpersonationProviderProps> = ({ children }) => {
  const [session, setSession] = useState<ImpersonationSession>({
    is_impersonating: false,
    learner: null,
    expires_at: null,
    session_id: null,
    teacher: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check impersonation status on mount
  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const response = await axios.get('/api/impersonate/status/');
      setSession(response.data);

      // Store token in localStorage if impersonating
      if (response.data.is_impersonating && response.data.session_id) {
        const token = localStorage.getItem('impersonationToken');
        if (!token) {
          // Token should already be set, but just in case
          console.warn('Impersonation active but no token in localStorage');
        }
      } else {
        localStorage.removeItem('impersonationToken');
      }
    } catch (err) {
      console.error('Error checking impersonation status:', err);
      setSession({
        is_impersonating: false,
        learner: null,
        expires_at: null,
        session_id: null,
        teacher: null,
      });
    }
  };

  const startImpersonation = async (learnerId: number, reason?: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post('/api/impersonate/start/', {
        learner_id: learnerId,
        reason: reason || ''
      });

      if (response.data.success) {
        // Store the impersonation token
        localStorage.setItem('impersonationToken', response.data.token);

        // Update session state
        await checkStatus();

        // Reload the page to refresh the UI with learner perspective
        window.location.reload();
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to start impersonation';
      setError(errorMsg);
      console.error('Error starting impersonation:', err);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const endImpersonation = async () => {
    setLoading(true);
    setError(null);

    // Try the server-side end. Don't let any server error stop the
    // client-side cleanup — the user's intent ("I want out") must
    // always be honoured. Most common failure: 400 "No active
    // impersonation session" because the token was wiped from
    // localStorage by AuthContext's poll (sessions can drift between
    // the two contexts). In that case the server says we're already
    // ended, which is exactly what we want.
    try {
      await axios.post('/api/impersonate/end/');
    } catch (err: any) {
      const status = err.response?.status;
      const serverMsg = err.response?.data?.error;
      if (status === 400) {
        console.warn(
          '[Impersonation] Server reports no active session; cleaning up client only.',
          serverMsg,
        );
      } else {
        const errorMsg = serverMsg || 'Failed to end impersonation server-side; cleaning up locally.';
        setError(errorMsg);
        console.error('[Impersonation] Server end failed:', err);
      }
    } finally {
      // Always clean up the client and reload — escape hatch.
      localStorage.removeItem('impersonationToken');
      setSession({
        is_impersonating: false,
        learner: null,
        expires_at: null,
        session_id: null,
        teacher: null,
      });
      setLoading(false);
      window.location.reload();
    }
  };

  return (
    <ImpersonationContext.Provider
      value={{
        session,
        startImpersonation,
        endImpersonation,
        checkStatus,
        loading,
        error,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
};
